import { ChangeDetectionStrategy, Component, computed, effect, inject, untracked } from "@angular/core";
import type { Feature } from "geojson";
import { MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { FloatingPanelComponent } from "../floating-panel/floating-panel.component";
import { FeaturePropertiesEditorComponent } from "../feature-properties-editor/feature-properties-editor.component";
import { PanelRotateControlComponent } from "../panel-rotate-control/panel-rotate-control.component";
import { MapInteractionService } from "../../ai-components/chat/services/map-interaction.service";
import { GeoService } from "../../viewer-components/services/internal-services/geo.service";
import { FeatureDetailPanelService } from "./feature-detail-panel.service";

/**
 * Floating panel showing one feature at a time from a layer, with prev/next rotation and
 * (when supported by the source layer) properties editing. Place inside a `position: relative`
 * container, same as `lib-layer-feature-panel`. Visibility is driven by FeatureDetailPanelService.
 * Fits the map to each feature automatically as you rotate; the header button re-triggers it
 * on demand (e.g. after the user has panned/zoomed away).
 */
@Component({
  selector: "lib-feature-detail-panel",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FloatingPanelComponent, FeaturePropertiesEditorComponent, PanelRotateControlComponent, MatIconButton, MatIcon, MatTooltip],
  templateUrl: "./feature-detail-panel.component.html",
})
export class FeatureDetailPanelComponent {
  protected readonly panelService = inject(FeatureDetailPanelService);
  private readonly mapInteraction = inject(MapInteractionService);

  protected readonly subtitle = computed(() => this.panelService.currentFeature()?.geometry?.type ?? "—");

  constructor() {
    effect(() => {
      const feature = this.panelService.currentFeature();
      const sourceEpsg = this.panelService.state()?.sourceEpsg ?? 4326;
      untracked(() => this.zoomToFeature(feature, sourceEpsg));
    });
  }

  protected onSaveProperties(properties: Record<string, unknown>): void {
    this.panelService.updateFeatureProperties(properties);
  }

  protected onFitToExtent(): void {
    const feature = this.panelService.currentFeature();
    const sourceEpsg = this.panelService.state()?.sourceEpsg ?? 4326;
    this.zoomToFeature(feature, sourceEpsg);
  }

  private zoomToFeature(feature: Feature | null, sourceEpsg: 4326 | 3857): void {
    if (!feature?.geometry) return;
    try {
      const wgs84Feature = sourceEpsg === 3857 ? GeoService.toWgs84(feature, false) : feature;
      const [minX, minY, maxX, maxY] = GeoService.bbox(wgs84Feature.geometry);
      this.mapInteraction.zoomToExtent(minX, minY, maxX, maxY);
    } catch (err) {
      console.error("FeatureDetailPanelComponent: failed to compute bbox", err);
    }
  }
}
